import { type FC, useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { InfoIcon } from "@phosphor-icons/react";
import { startCase } from "lodash";
import { z } from "zod";

import {
  Button,
  Checkbox,
  CheckboxState,
  Form,
  Tooltip,
  toastSuccess,
  useModal,
  useUIForm,
} from "@alpacahq/ui/old";

import { useGetMargin, useMutationRequest } from "~/v2/api/hooks";
import {
  type Account,
  createPaperAccount,
  type createPaperAccountParams,
  type PaperAccount,
} from "~/v2/api/rest/account";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { getPrettyCash } from "~/v2/utils/formatting";

export interface CreatePaperAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MIN_FUNDS = 1;
const MAX_FUNDS = 1000000;

const schema = z.object({
  cash: z.coerce.number().gte(MIN_FUNDS).lte(MAX_FUNDS).optional(),
  name: z.string(),
  match: z.boolean(),
  matchAccountId: z.string().optional(),
});

type CreatePaperAccountModalForm = z.infer<typeof schema>;

const getAccountLabel = (account: Account) => {
  // Nickname
  if (account.account_name) {
    return `${account.account_name} - ${account.account_number}`;
  }

  if (account.account_type === "ira") {
    return `${account.account_sub_type ? `${startCase(account.account_sub_type)} ` : ""}IRA - ${account.account_number}`;
  }

  if (account.account_type === "legal_entity") {
    return `Business Trading - ${account.account_number}`;
  }

  return `Individual Trading - ${account.account_number}`;
};

export const CreatePaperAccountModal: FC<CreatePaperAccountModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { refetchPaperAccounts, liveAccounts } = useAccountContext();

  const createPaperAccountMutation = useMutationRequest<
    PaperAccount,
    createPaperAccountParams
  >((formValues) => createPaperAccount(formValues), {
    onSuccess: () => {
      toastSuccess("You have successfully created a new account");
      refetchPaperAccounts();
      onClose();
    },
  });

  const form = useUIForm<CreatePaperAccountModalForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      match: false,
    },
  });

  const matchLiveEquity = form.watch("match");
  const matchAccountId = form.watch("matchAccountId") ?? liveAccounts.at(0)?.id;

  const { margin, isLoading: isMarginLoading } = useGetMargin({
    accountID: matchAccountId,
    paper: false,
    options: {
      enabled: !!matchAccountId,
    },
  });

  const handleSubmit = ({ match, cash, name }: CreatePaperAccountModalForm) => {
    const values = {
      cash: match ? parseFloat(margin?.equity ?? "0") : cash ? cash : 100000,
      name,
    };
    createPaperAccountMutation.mutate(values);
  };

  const liveAccountOptions = useMemo(() => {
    return (
      liveAccounts?.map((account) => ({
        label: getAccountLabel(account),
        value: account.id,
      })) ?? []
    );
  }, [liveAccounts]);

  const isSubmitDisabled =
    createPaperAccountMutation.isPending ||
    (matchLiveEquity && isMarginLoading);

  const {
    component: Modal,
    isOpen: isModalOpen,
    toggle,
  } = useModal({
    size: "sm",
    title: "Open New Paper Account",
    content: (
      <Form formMethods={form} onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          <p className="m-0 p-0 font-medium text-gray-700 text-sm">
            Create a new paper account to simulate trades and test strategies.
            You can have up to 3 paper accounts.
          </p>

          <Form.TextInput label="Nickname" name="name" />

          <Form.TextInput
            type="number"
            label="Set Funds"
            name="cash"
            isDisabled={matchLiveEquity}
            placeholder={`${getPrettyCash(MIN_FUNDS)} - ${getPrettyCash(
              MAX_FUNDS,
            )}`}
          />

          {liveAccountOptions.length > 0 && (
            <>
              <div className="flex items-center gap-1">
                <label
                  className="flex cursor-pointer items-start justify-start gap-1"
                  onClick={() => {
                    form.setValue("match", !matchLiveEquity);
                  }}
                >
                  <Checkbox
                    className="h-5 w-5"
                    state={
                      matchLiveEquity
                        ? CheckboxState.Checked
                        : CheckboxState.Unchecked
                    }
                  />
                  <span className="font-semibold text-gray-800 text-sm">
                    Sync to your live account balance
                  </span>
                </label>
                <Tooltip content="Trade configurations must be set manually to match your live account.">
                  <InfoIcon size={16} />
                </Tooltip>
              </div>
              <Form.SelectInput
                name="liveAccount"
                disabled={!matchLiveEquity}
                placeholder={liveAccountOptions.at(0)?.label}
                options={liveAccountOptions}
                onChange={(id: string) => form.setValue("matchAccountId", id)}
              />
            </>
          )}
        </div>
      </Form>
    ),
    controls: (
      <div className="flex gap-4">
        <Button variant="v2/stroke" onClick={() => toggle()}>
          Cancel
        </Button>
        <Button
          variant="v2/filled"
          onClick={form.handleSubmit(handleSubmit)}
          disabled={isSubmitDisabled}
          loading={isSubmitDisabled}
        >
          Save
        </Button>
      </div>
    ),
    onClose: () => {
      if (isModalOpen) {
        onClose();
      }
    },
  });

  useEffect(() => {
    if (isOpen && !isModalOpen) {
      toggle();
    }
  }, [isOpen, isModalOpen, toggle]);

  return <>{Modal}</>;
};
export default CreatePaperAccountModal;
